import { useState } from 'react';
import { ExternalLink, FileText, Inbox, Plus } from 'lucide-react';
import type { FileAttachmentTypeT } from '@lotushealth/lotushealth_js_sdk/schemas';

import {
  DocumentDraftCard,
  REFERRAL_DRAFT_STORE,
  useDocumentDraftsQuery,
} from '@/entities/document-draft';
import { useAttachmentViewUrlQuery } from '@/entities/file-attachments';
import { openReferralEditor } from '@/shared/stores';
import { DocumentCard } from '@/shared/ui/document-editor';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/shadcn/tabs';
import { WorkspaceSection } from '@/shared/ui/primitives';

import { usePatientReferralsQuery } from '../model/usePatientReferralsQuery';
import { usePatientOutboundReferralsQuery } from '../model/usePatientOutboundReferralsQuery';
import type { PatientReferral } from '../api/fetchPatientReferrals';
import styles from './PatientReferralsTab.module.css';

interface PatientReferralsTabProps {
  userId: string;
}

const REFERRAL_DIRECTIONS = [
  { id: 'drafts', label: 'Drafts', emptyLabel: 'No referral drafts' },
  { id: 'sent', label: 'Sent', emptyLabel: 'No sent referrals' },
  { id: 'received', label: 'Received', emptyLabel: 'No received referrals' },
] as const;

type ReferralDirection = (typeof REFERRAL_DIRECTIONS)[number]['id'];

function formatDate(value: string | null) {
  if (!value) {
    return 'Date unavailable';
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value)
  );
}

function sourceLabel(referral: PatientReferral) {
  const part = referral.communication.methodSpecificPart;
  return part.type === 'efax' ? `eFax · ${part.remoteFaxNumber}` : 'Metriport message';
}

function referralTitle(referral: PatientReferral) {
  return referral.communication.subject.trim() || 'Untitled referral';
}

type ReferralAttachmentInfo = {
  id: string | null;
  fileType: FileAttachmentTypeT;
  filename: string;
};

function attachmentInfo(attachment: unknown, index: number): ReferralAttachmentInfo {
  let id: string | null = null;
  let fileType: FileAttachmentTypeT = 'document';
  let filename = `PDF attachment ${index + 1}`;

  if (typeof attachment === 'object' && attachment !== null) {
    const attachmentId = (attachment as { id?: unknown }).id;
    if (typeof attachmentId === 'string') {
      id = attachmentId;
    }
    const metadata = (attachment as { metadata?: unknown }).metadata;
    if (typeof metadata === 'object' && metadata !== null) {
      const metadataFileType = (metadata as { file_type?: unknown }).file_type;
      if (
        metadataFileType === 'document' ||
        metadataFileType === 'image' ||
        metadataFileType === 'audio'
      ) {
        fileType = metadataFileType;
      }
      const preUpload = (metadata as { pre_upload?: unknown }).pre_upload;
      if (typeof preUpload === 'object' && preUpload !== null) {
        const attachmentFilename = (preUpload as { filename?: unknown }).filename;
        if (typeof attachmentFilename === 'string' && attachmentFilename.trim()) {
          filename = attachmentFilename;
        }
      }
    }
  }

  return { id, fileType, filename };
}

function attachmentLabel(attachment: unknown, index: number) {
  return attachmentInfo(attachment, index).filename;
}

function ReferralCard({
  userId,
  referral,
  onSelect,
}: {
  userId: string;
  referral: PatientReferral;
  onSelect: () => void;
}) {
  const communication = referral.communication;
  const firstAttachment = communication.attachments[0];
  const info = firstAttachment === undefined ? null : attachmentInfo(firstAttachment, 0);
  const viewQuery = useAttachmentViewUrlQuery({
    userId,
    attachmentId: info?.id ?? '',
    fileType: info?.fileType ?? 'document',
    enabled: Boolean(info?.id),
  });
  const attachmentCount = communication.attachments.length;
  const received = formatDate(communication.receivedAt ?? communication.createdAt);
  const title = referralTitle(referral);
  return (
    <DocumentCard
      title={title}
      details={[
        sourceLabel(referral),
        attachmentCount > 0
          ? `${received} · ${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`
          : received,
      ]}
      previewUrl={viewQuery.data?.viewUrl ?? null}
      previewLoading={Boolean(info?.id) && viewQuery.isPending}
      ariaLabel={`View ${title}`}
      onSelect={onSelect}
    />
  );
}

function ReferralDetails({ userId, referral }: { userId: string; referral: PatientReferral }) {
  const communication = referral.communication;
  const [selectedAttachmentIndex, setSelectedAttachmentIndex] = useState(0);
  const selectedAttachment = communication.attachments[selectedAttachmentIndex];
  const selectedAttachmentInfo = selectedAttachment
    ? attachmentInfo(selectedAttachment, selectedAttachmentIndex)
    : null;
  const attachmentViewQuery = useAttachmentViewUrlQuery({
    userId,
    attachmentId: selectedAttachmentInfo?.id ?? '',
    fileType: selectedAttachmentInfo?.fileType ?? 'document',
    enabled: Boolean(selectedAttachmentInfo?.id),
  });
  const selectedAttachmentUrl = attachmentViewQuery.data?.viewUrl ?? null;
  return (
    <div className={styles.detailsLayout}>
      <div className={styles.details}>
        <div className={styles.detailRow}>
          <span>Source</span>
          <span>{sourceLabel(referral)}</span>
        </div>
        <div className={styles.detailRow}>
          <span>{communication.direction === 'outbound' ? 'Sent' : 'Received'}</span>
          <span>{formatDate(communication.receivedAt ?? communication.createdAt)}</span>
        </div>
        <div className={styles.message}>
          <span className={styles.detailLabel}>Message</span>
          <p>{communication.body?.trim() || 'No message body was included.'}</p>
        </div>
        <div className={styles.message}>
          <span className={styles.detailLabel}>Attachments</span>
          {communication.attachments.length === 0 ? (
            <p>No attachments.</p>
          ) : (
            <div className={styles.attachmentList}>
              {communication.attachments.map((attachment, index) => (
                <button
                  key={index}
                  type="button"
                  className={`${styles.attachmentItem} ${
                    index === selectedAttachmentIndex ? styles.selectedAttachment : ''
                  }`}
                  onClick={() => setSelectedAttachmentIndex(index)}
                  aria-pressed={index === selectedAttachmentIndex}
                >
                  <FileText className="size-4" aria-hidden="true" />
                  <span className={styles.attachmentName}>
                    {index + 1}. {attachmentLabel(attachment, index)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {selectedAttachmentInfo && (
        <div className={styles.documentSection}>
          <div className={styles.documentHeading}>
            <span>
              Attachment {selectedAttachmentIndex + 1} of {communication.attachments.length}
            </span>
            {selectedAttachmentUrl && (
              <a
                href={selectedAttachmentUrl}
                target="_blank"
                rel="noreferrer"
                className={styles.openDocument}
              >
                Open PDF <ExternalLink className="size-3.5" aria-hidden="true" />
              </a>
            )}
          </div>
          <div className={styles.documentViewer}>
            {attachmentViewQuery.isPending ? (
              <p className={styles.documentState}>Loading attachment…</p>
            ) : selectedAttachmentUrl ? (
              <iframe
                src={`${selectedAttachmentUrl}#toolbar=1&navpanes=1&zoom=page-fit`}
                title={`${referralTitle(referral)} attachment ${selectedAttachmentIndex + 1}`}
                className={styles.documentFrame}
              />
            ) : (
              <p className={styles.documentState}>Attachment preview unavailable.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PatientReferralsTab({ userId }: PatientReferralsTabProps) {
  const query = usePatientReferralsQuery(userId);
  const outboundQuery = usePatientOutboundReferralsQuery(userId);
  const draftsQuery = useDocumentDraftsQuery(userId, REFERRAL_DRAFT_STORE);
  const drafts = draftsQuery.data ?? [];
  const [direction, setDirection] = useState<ReferralDirection>('received');
  const [selectedReferral, setSelectedReferral] = useState<PatientReferral | null>(null);
  const referrals = query.data ?? [];
  const outboundReferrals = outboundQuery.data ?? [];
  return (
    <WorkspaceSection
      title="Referrals"
      action={
        <Button type="button" size="sm" onClick={() => openReferralEditor(userId)}>
          <Plus className="mr-1.5 size-3.5" aria-hidden="true" />
          New referral
        </Button>
      }
    >
      <Tabs
        value={direction}
        onValueChange={(value) => setDirection(value as ReferralDirection)}
        className={styles.tabs}
      >
        <TabsList aria-label="Referral direction">
          {REFERRAL_DIRECTIONS.map(({ id, label }) => (
            <TabsTrigger key={id} value={id}>
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {REFERRAL_DIRECTIONS.map(({ id, emptyLabel }) => (
          <TabsContent key={id} value={id}>
            {id === 'drafts' && draftsQuery.isLoading ? (
              <p className={styles.emptyState} role="status">
                Loading drafts...
              </p>
            ) : id === 'drafts' && draftsQuery.isError ? (
              <p className={styles.emptyState} role="status">
                Unable to load drafts
              </p>
            ) : id === 'drafts' && drafts.length > 0 ? (
              <div className={styles.list}>
                {drafts.map((draft) => (
                  <DocumentDraftCard
                    key={draft.id}
                    patientId={userId}
                    draft={draft}
                    onSelect={() => openReferralEditor(userId, draft.id)}
                  />
                ))}
              </div>
            ) : id === 'sent' && outboundQuery.isLoading ? (
              <p className={styles.emptyState} role="status">
                Loading sent referrals...
              </p>
            ) : id === 'sent' && outboundQuery.isError ? (
              <p className={styles.emptyState} role="status">
                Unable to load sent referrals
              </p>
            ) : id === 'sent' && outboundReferrals.length === 0 ? (
              <div className={styles.emptyState}>
                <Inbox className="size-5" aria-hidden="true" />
                <span>No sent referrals</span>
              </div>
            ) : id === 'sent' ? (
              <div className={styles.list}>
                {outboundReferrals.map((referral) => (
                  <ReferralCard
                    key={referral.id}
                    userId={userId}
                    referral={referral}
                    onSelect={() => setSelectedReferral(referral)}
                  />
                ))}
              </div>
            ) : id !== 'received' ? (
              <p className={styles.emptyState} role="status">
                <Inbox className="size-5" aria-hidden="true" />
                {emptyLabel}
              </p>
            ) : query.isLoading ? (
              <p className={styles.emptyState} role="status">
                Loading referrals...
              </p>
            ) : query.isError ? (
              <p className={styles.emptyState} role="status">
                Unable to load referrals
              </p>
            ) : referrals.length === 0 ? (
              <div className={styles.emptyState}>
                <Inbox className="size-5" aria-hidden="true" />
                <span>{emptyLabel}</span>
              </div>
            ) : (
              <div className={styles.list}>
                {referrals.map((referral) => (
                  <ReferralCard
                    key={referral.id}
                    userId={userId}
                    referral={referral}
                    onSelect={() => setSelectedReferral(referral)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog
        open={selectedReferral !== null}
        onOpenChange={(open) => !open && setSelectedReferral(null)}
      >
        <DialogContent className={styles.dialog}>
          <DialogHeader>
            <DialogTitle>
              {selectedReferral ? referralTitle(selectedReferral) : 'Referral details'}
            </DialogTitle>
            <DialogDescription>
              {selectedReferral ? sourceLabel(selectedReferral) : null}
            </DialogDescription>
          </DialogHeader>
          {selectedReferral && <ReferralDetails userId={userId} referral={selectedReferral} />}
          <Button type="button" variant="outline" onClick={() => setSelectedReferral(null)}>
            Close
          </Button>
        </DialogContent>
      </Dialog>
    </WorkspaceSection>
  );
}
